import { useEffect, useRef, useState } from "react";
import type { FreshAchievement } from "./kpiTypes";

/**
 * Marks newly unlocked achievements as "fresh" exactly once per id. The
 * consumer renders an `aria-live` region with the achievement labels so screen
 * readers announce the celebration alongside the visual sparkle.
 */
export function useFreshAchievementToasts(fresh: FreshAchievement[] | null | undefined): {
  freshById: Set<string>;
  liveMessage: string;
} {
  const seenRef = useRef<Set<string>>(new Set());
  const [snapshot, setSnapshot] = useState<{ ids: Set<string>; liveMessage: string }>({
    ids: new Set(),
    liveMessage: "",
  });

  useEffect(() => {
    if (!fresh || fresh.length === 0) {
      if (snapshot.ids.size > 0 || snapshot.liveMessage) {
        setSnapshot({ ids: new Set(), liveMessage: "" });
      }
      return;
    }
    const newOnes = fresh.filter((a) => !seenRef.current.has(a.id));
    if (newOnes.length === 0) return;
    for (const n of newOnes) seenRef.current.add(n.id);
    const ids = new Set(newOnes.map((a) => a.id));
    const liveMessage = `Achievement${newOnes.length > 1 ? "s" : ""} unlocked: ${newOnes
      .map((a) => a.achievement_key.replaceAll("_", " "))
      .join(", ")}`;
    setSnapshot({ ids, liveMessage });
  }, [fresh, snapshot.ids.size, snapshot.liveMessage]);

  return { freshById: snapshot.ids, liveMessage: snapshot.liveMessage };
}
